/**
 * COA Request Page JavaScript
 * Handles product search, selection, and form submission to Next.js API
 *
 * API Endpoint: POST /apps/coa-request (via Shopify app proxy)
 * Fallback: POST https://alliance-tree.vercel.app/api/coa/customer-request
 */

(function() {
  'use strict';

  // API Configuration
  const API_ENDPOINTS = [
    '/apps/coa-request',  // Shopify app proxy (preferred)
    'https://my-coa.alliancechemical.com/api/coa/customer-request'  // Direct fallback
  ];

  // State
  let selectedProducts = [];
  const MAX_PRODUCTS = 10;

  // DOM Elements
  const form = document.getElementById('coa-request-form');
  const searchInput = document.getElementById('coa-product-search');
  const resultsContainer = document.getElementById('coa-product-results');
  const selectedContainer = document.getElementById('coa-selected-products');
  const errorContainer = document.getElementById('coa-error');
  const successContainer = document.getElementById('coa-success');
  const submitBtn = form ? form.querySelector('.coa-submit-btn') : null;

  // Get products from server-rendered data
  const allProducts = window.coaPageProducts || [];

  // Process products for search
  const processedProducts = allProducts.map((p, index) => {
    const options = (p.options || []).filter(o => o != null).join(' ');
    const displayName = p.variantTitle && p.variantTitle !== 'Default Title'
      ? `${p.productTitle} - ${p.variantTitle}`
      : p.productTitle;

    return {
      ...p,
      id: index,
      displayName: displayName,
      searchString: `${p.productTitle} ${options} ${p.sku || ''}`.toLowerCase()
    };
  });

  // Sort products alphabetically
  processedProducts.sort((a, b) => a.displayName.localeCompare(b.displayName));

  /**
   * Filter products based on search query
   */
  function filterProducts(query) {
    const q = query.toLowerCase().trim();
    if (!q) return processedProducts.slice(0, 50); // Show first 50 when empty

    return processedProducts.filter(p => p.searchString.includes(q)).slice(0, 50);
  }

  /**
   * Render search results
   */
  function renderResults(products) {
    if (products.length === 0) {
      resultsContainer.innerHTML = '<div class="coa-product-result" style="cursor:default;">No products found</div>';
      return;
    }

    resultsContainer.innerHTML = products.map(p => `
      <div class="coa-product-result" data-product-id="${p.id}">
        <div class="coa-product-result-title">${escapeHtml(p.displayName)}</div>
        ${p.sku ? `<div class="coa-product-result-sku">SKU: ${escapeHtml(p.sku)}</div>` : ''}
      </div>
    `).join('');
  }

  /**
   * Render selected products
   */
  function renderSelectedProducts() {
    if (selectedProducts.length === 0) {
      selectedContainer.innerHTML = '';
      return;
    }

    selectedContainer.innerHTML = selectedProducts.map((p, index) => `
      <div class="coa-product-card" data-index="${index}">
        <div class="coa-product-card-info">
          <div class="coa-product-card-title">${escapeHtml(p.displayName)}</div>
          ${p.sku ? `<div class="coa-product-card-sku">SKU: ${escapeHtml(p.sku)}</div>` : ''}
        </div>
        <div class="coa-product-card-lot">
          <label>LOT # <span class="required">*</span></label>
          <input type="text" class="lot-input" data-index="${index}" placeholder="Enter LOT number" value="${escapeHtml(p.lotNumber || '')}">
        </div>
        <button type="button" class="coa-product-card-remove" data-index="${index}">&times;</button>
      </div>
    `).join('');

    // Update progress indicator
    updateProgress();
  }

  /**
   * Add product to selection
   */
  function addProduct(productId) {
    if (selectedProducts.length >= MAX_PRODUCTS) {
      showError(`Maximum ${MAX_PRODUCTS} products allowed per request.`);
      return;
    }

    const product = processedProducts.find(p => p.id === productId);
    if (!product) return;

    // Check if already selected
    if (selectedProducts.some(p => p.id === productId)) {
      showError('This product is already selected.');
      return;
    }

    selectedProducts.push({ ...product, lotNumber: '' });
    renderSelectedProducts();
    hideResults();
    searchInput.value = '';
    hideError();
  }

  /**
   * Remove product from selection
   */
  function removeProduct(index) {
    selectedProducts.splice(index, 1);
    renderSelectedProducts();
  }

  /**
   * Update LOT number for a product
   */
  function updateLotNumber(index, value) {
    if (selectedProducts[index]) {
      selectedProducts[index].lotNumber = value;
    }
  }

  /**
   * Update progress indicator
   */
  function updateProgress() {
    const steps = document.querySelectorAll('.coa-progress-step');
    const orderNumber = document.getElementById('coa-order-number').value.trim();
    const hasProducts = selectedProducts.length > 0;
    const name = document.getElementById('coa-name').value.trim();
    const email = document.getElementById('coa-email').value.trim();

    steps[0].classList.toggle('completed', !!orderNumber);
    steps[0].classList.toggle('active', !orderNumber);

    steps[1].classList.toggle('completed', hasProducts);
    steps[1].classList.toggle('active', !!orderNumber && !hasProducts);

    steps[2].classList.toggle('completed', !!(name && email));
    steps[2].classList.toggle('active', hasProducts && !(name && email));
  }

  /**
   * Show/hide results dropdown
   */
  function showResults() {
    resultsContainer.classList.add('active');
  }

  function hideResults() {
    resultsContainer.classList.remove('active');
  }

  /**
   * Show/hide error message
   */
  function showError(message) {
    errorContainer.textContent = message;
    errorContainer.classList.add('active');
  }

  function hideError() {
    errorContainer.classList.remove('active');
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Validate form
   */
  function validateForm() {
    hideError();

    const orderNumber = document.getElementById('coa-order-number').value.trim();
    const name = document.getElementById('coa-name').value.trim();
    const email = document.getElementById('coa-email').value.trim();

    if (!orderNumber) {
      showError('Please enter your order number.');
      return false;
    }

    if (selectedProducts.length === 0) {
      showError('Please select at least one product.');
      return false;
    }

    // Check all LOT numbers
    let allLotsValid = true;
    document.querySelectorAll('.lot-input').forEach((input, index) => {
      if (!input.value.trim()) {
        input.classList.add('error');
        allLotsValid = false;
      } else {
        input.classList.remove('error');
        selectedProducts[index].lotNumber = input.value.trim();
      }
    });

    if (!allLotsValid) {
      showError('Please enter LOT numbers for all selected products.');
      return false;
    }

    if (!name) {
      showError('Please enter your name.');
      return false;
    }

    if (!email) {
      showError('Please enter your email address.');
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showError('Please enter a valid email address.');
      return false;
    }

    return true;
  }

  /**
   * Build API payload for each product request
   * API expects individual requests per product/LOT combination
   */
  function buildPayloads() {
    const orderNumber = document.getElementById('coa-order-number').value.trim();
    const name = document.getElementById('coa-name').value.trim();
    const email = document.getElementById('coa-email').value.trim();
    const company = document.getElementById('coa-company').value.trim();

    return selectedProducts.map(p => ({
      // Product identification
      productHandle: p.productHandle || '',
      shopifyProductId: p.shopifyProductId || '',
      shopifyVariantId: p.shopifyVariantId || '',

      // Required fields
      lotNumber: p.lotNumber,
      requesterEmail: email,

      // Optional fields
      requesterName: name,
      companyName: company,
      orderNumber: orderNumber,

      // Additional context
      productTitle: p.displayName,
      sku: p.sku || ''
    }));
  }

  /**
   * Submit request to Next.js API
   */
  async function submitToAPI(payload) {
    let lastError = null;

    for (const endpoint of API_ENDPOINTS) {
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || data.message || `Request failed with status ${response.status}`);
        }

        return { success: true, data };
      } catch (err) {
        lastError = err;
        console.warn(`API endpoint ${endpoint} failed:`, err.message);
        // Continue to next endpoint
      }
    }

    throw lastError || new Error('All API endpoints failed');
  }

  /**
   * Track with Klaviyo
   */
  function trackKlaviyo() {
    if (typeof klaviyo === 'undefined' && typeof _learnq === 'undefined') return;

    try {
      const email = document.getElementById('coa-email').value.trim();
      const name = document.getElementById('coa-name').value.trim();
      const orderNumber = document.getElementById('coa-order-number').value.trim();

      const kl = window.klaviyo || window._learnq;
      if (kl) {
        kl.push(['identify', { '$email': email, '$first_name': name }]);
        kl.push(['track', 'COA Request Submitted', {
          'Order Number': orderNumber,
          'Product Count': selectedProducts.length,
          'Products': selectedProducts.map(p => ({
            'Name': p.displayName,
            'SKU': p.sku,
            'LOT Number': p.lotNumber
          }))
        }]);
      }
    } catch (e) {
      console.warn('Klaviyo tracking failed:', e);
    }
  }

  /**
   * Set loading state on submit button
   */
  function setLoading(loading) {
    if (!submitBtn) return;

    if (loading) {
      submitBtn.disabled = true;
      submitBtn.dataset.originalText = submitBtn.textContent;
      submitBtn.innerHTML = `
        <svg class="coa-spinner" width="20" height="20" viewBox="0 0 24 24" style="animation: coa-spin 1s linear infinite; margin-right: 8px;">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none" opacity="0.25"/>
          <path d="M12 2a10 10 0 019.95 9" stroke="currentColor" stroke-width="3" fill="none" stroke-linecap="round"/>
        </svg>
        Submitting...
      `;
    } else {
      submitBtn.disabled = false;
      submitBtn.textContent = submitBtn.dataset.originalText || 'Submit COA Request';
    }
  }

  // Event Listeners

  // Search input
  searchInput.addEventListener('focus', function() {
    renderResults(filterProducts(this.value));
    showResults();
  });

  searchInput.addEventListener('input', function() {
    renderResults(filterProducts(this.value));
    showResults();
  });

  // Click outside to close results
  document.addEventListener('click', function(e) {
    if (!searchInput.contains(e.target) && !resultsContainer.contains(e.target)) {
      hideResults();
    }
  });

  // Select product from results
  resultsContainer.addEventListener('click', function(e) {
    const result = e.target.closest('.coa-product-result');
    if (result && result.dataset.productId) {
      addProduct(parseInt(result.dataset.productId, 10));
    }
  });

  // Remove product / update LOT number
  selectedContainer.addEventListener('click', function(e) {
    const removeBtn = e.target.closest('.coa-product-card-remove');
    if (removeBtn) {
      removeProduct(parseInt(removeBtn.dataset.index, 10));
    }
  });

  selectedContainer.addEventListener('input', function(e) {
    if (e.target.classList.contains('lot-input')) {
      updateLotNumber(parseInt(e.target.dataset.index, 10), e.target.value);
      e.target.classList.remove('error');
    }
  });

  // Update progress on form field changes
  ['coa-order-number', 'coa-name', 'coa-email'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.addEventListener('input', updateProgress);
  });

  // Form submission
  form.addEventListener('submit', async function(e) {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    hideError();

    try {
      // Build payloads for each product
      const payloads = buildPayloads();

      // Submit each product request
      // Note: If API supports batch, we could send all at once
      // For now, submitting individually for each product/LOT combination
      const results = [];

      for (const payload of payloads) {
        try {
          const result = await submitToAPI(payload);
          results.push({ success: true, product: payload.productTitle, data: result.data });
        } catch (err) {
          results.push({ success: false, product: payload.productTitle, error: err.message });
        }
      }

      // Check results
      const failures = results.filter(r => !r.success);

      if (failures.length === 0) {
        // All succeeded
        trackKlaviyo();
        form.style.display = 'none';
        successContainer.classList.add('active');

        // Update success message with details
        const successMessage = successContainer.querySelector('p');
        if (successMessage) {
          successMessage.textContent = `We've received your COA request for ${results.length} product${results.length > 1 ? 's' : ''}. You'll receive an email at ${document.getElementById('coa-email').value} shortly.`;
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (failures.length < results.length) {
        // Partial success
        trackKlaviyo();
        const successCount = results.length - failures.length;
        showError(`${successCount} of ${results.length} requests submitted successfully. Failed: ${failures.map(f => f.product).join(', ')}. Please try again for the failed products.`);
      } else {
        // All failed
        throw new Error(failures[0]?.error || 'All requests failed. Please try again.');
      }
    } catch (err) {
      console.error('COA submission error:', err);
      showError(err.message || 'There was an error submitting your request. Please try again.');
    } finally {
      setLoading(false);
    }
  });

  // Initial render
  updateProgress();

  // Add spinner animation style
  const style = document.createElement('style');
  style.textContent = `
    @keyframes coa-spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    .coa-spinner {
      display: inline-block;
      vertical-align: middle;
    }
  `;
  document.head.appendChild(style);

})();
